/**
 * Load Data Pipeline → Lane Explorer.
 *
 * Lane-level detail, which is what makes the data checkable by hand: pick a
 * lane you know, see whether the rate the pipeline stored matches the market
 * you expect.
 *
 * CAPPED WINDOW, DELIBERATELY. This is an audit of what the consumer wrote, so
 * it reads raw hourly rows — which the maintenance cycle keeps for 48 h before
 * rolling them into daily. The server clamps the window to that. What a lane
 * PAYS over two weeks is Lane Prices, from the Market Intel API.
 */

import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import * as React from 'react'
import { loadPipelineApi } from 'api/loadPipeline'
import { ErrorState, Loading, WindowPicker, useLoadPipelineData, formatNumber } from './components/shared'

export default function LoadPipelineLanesPage() {
    const [hours, setHours] = React.useState(24)
    const [search, setSearch] = React.useState('')
    const [debounced, setDebounced] = React.useState('')

    // Debounced so typing a lane name is one query, not one per keystroke —
    // this hits a small instance that is also absorbing the consumer's writes.
    React.useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 400)
        return () => clearTimeout(t)
    }, [search])

    const { data, error, loading } = useLoadPipelineData(() => loadPipelineApi.getLanes(hours, 100, debounced), [hours, debounced])

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="body-sm" textColor="text.tertiary">
                    Directional lanes — <code>origin&gt;destination</code>. Chicago→Dallas is a different lane from Dallas→Chicago, because the rates are.
                </Typography>
                <WindowPicker hours={hours} onChange={setHours} maxHours={data?.maxWindowHours ?? 48} />
            </Box>

            <Input
                size="sm"
                placeholder="Filter lanes, e.g. us-il-chicago or dallas"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2, maxWidth: 420 }}
            />

            {error ? <ErrorState error={error} /> : null}

            {data && data.maxWindowHours <= 48 ? (
                <Card variant="soft" color="neutral" sx={{ mb: 2 }}>
                    <Typography level="body-xs">
                        Windows are capped at {data.maxWindowHours}h here: these figures audit raw hourly rows, which are kept for 48 h before being rolled into
                        daily. For what a lane pays over the 14-day window, use Lane Prices.
                    </Typography>
                </Card>
            ) : null}

            {loading && !data ? <Loading /> : null}

            {data ? (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2 }}>
                    <Table size="sm" stickyHeader hoverRow>
                        <thead>
                            <tr>
                                <th>Lane</th>
                                <th style={{ width: 90 }}>Equip</th>
                                <th style={{ width: 130, textAlign: 'right' }}>Observations</th>
                                <th style={{ width: 110, textAlign: 'right' }}>Avg $/mi</th>
                                <th style={{ width: 170, textAlign: 'right' }}>Last seen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.lanes.map((l) => (
                                <tr key={`${l.laneId}|${l.equipment}`}>
                                    <td>
                                        <Typography level="body-sm">
                                            <code>{l.laneId}</code>
                                        </Typography>
                                    </td>
                                    <td>
                                        <Chip size="sm" variant="soft">
                                            {l.equipment}
                                        </Chip>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{formatNumber(l.observations)}</td>
                                    <td style={{ textAlign: 'right' }}>{l.avgRpm === null ? '—' : `$${l.avgRpm.toFixed(2)}`}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <Typography level="body-xs">{new Date(l.lastSeen).toLocaleString()}</Typography>
                                    </td>
                                </tr>
                            ))}
                            {data.lanes.length === 0 ? (
                                <tr>
                                    <td colSpan={5}>
                                        <Typography level="body-sm" textColor="text.tertiary">
                                            No lanes match.
                                        </Typography>
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </Table>
                </Sheet>
            ) : null}
        </Box>
    )
}
